/**
 * Utilisateurs actifs par statut d'abonnement — barres horizontales.
 *
 * Trois catégories nominales, donc une seule teinte, comme pour la répartition
 * par action.
 *
 * Le segment « Compte supprimé » n'est pas une anomalie : ce sont des comptes
 * effacés côté prod dont l'activité passée subsiste dans le tracker (les deux
 * bases de prod n'ont aucune clé étrangère entre elles, et la jointure est un
 * LEFT JOIN exprès). Les masquer reviendrait à amputer l'historique, d'autant
 * plus qu'on remonte loin.
 */

import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { SUBSCRIPTION_LABELS } from '@shared/types/stats';

import { formatCompact, formatInteger } from '../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, COLORS } from '../lib/theme';
import type { DataColumn, SubscriptionSegmentItem } from '../types';
import { ChartCard } from './Card';
import { ChartTooltip } from './ChartTooltip';

interface Row extends SubscriptionSegmentItem {
  label: string;
}

const columns: ReadonlyArray<DataColumn<Row>> = [
  { key: 'label', header: 'Statut', render: (row) => row.label },
  {
    key: 'activeUsers',
    header: 'Actifs',
    numeric: true,
    render: (row) => formatInteger(row.activeUsers),
  },
  { key: 'actions', header: 'Actions', numeric: true, render: (row) => formatInteger(row.actions) },
];

export function SubscriptionSegmentsChart({ data }: { data: readonly SubscriptionSegmentItem[] }) {
  const rows: Row[] = data.map((item) => ({
    ...item,
    label: SUBSCRIPTION_LABELS[item.status] ?? item.status,
  }));

  return (
    <ChartCard
      title="Actifs par statut d'abonnement"
      subtitle="« Compte supprimé » = activité conservée, compte effacé côté prod"
      rows={rows}
      columns={columns}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 56, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={formatCompact}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fill: COLORS.textSecondary, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={130}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={<ChartTooltip valueFormatter={formatInteger} />}
          />
          <Bar
            isAnimationActive={false}
            dataKey="activeUsers"
            name="Utilisateurs actifs"
            fill={COLORS.series1}
            radius={[0, 4, 4, 0]}
            maxBarSize={BAR_MAX_SIZE}
          >
            <LabelList
              dataKey="activeUsers"
              position="right"
              fill={COLORS.textSecondary}
              fontSize={12}
              formatter={(value: unknown) => formatInteger(Number(value))}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
